import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';

import { Paper, Grid, Typography, TextField, Chip, IconButton, InputAdornment } from '@mui/material';
import { useFormik } from 'formik';
import Loader from 'components/Loader';
import Modal from 'components/Modal';
import Input from 'components/Input';
import Select from 'components/Select';
import Button from 'components/Button';
import SchedulingEvent from 'components/SchedulingEvent';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';

import { Close as CloseIcon, KeyboardArrowLeft, KeyboardArrowRight, EventNote } from '@material-ui/icons';

import { setToken } from 'services';
import { setUserId } from 'pages/Profile/store/actions';
import { setEstablishmentId } from 'pages/BusinessInfo/store/actions';
import { setCalendarOption } from 'pages/Scheduling/store/actions';
import { loadCalendarServices } from 'pages/Scheduling/store/thunk';
import { clearSchedulingOthers } from '../store/actions';
import { loadOthersScheduling, upsertOthersScheduling } from '../store/thunk';

import { capitalizeString } from 'helpers/formatters';
import useDidMount from 'helpers/hooks/useDidMount';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';
import useSchedulingAvailability from 'helpers/hooks/useSchedulingAvailability';

import { validations } from './validations';

import { theme } from 'theme/globalStyles';

const DrawerIcon = require('../../../assets/img/icon_drawer.svg');

function renderEventContent (eventInfo) {
  if(eventInfo.event.display === 'background' || eventInfo.event.extendedProps?.isUnavailableSlot) {
    return true;
  }

  const { title } = eventInfo.event._def;
  const { quantity, time, startDateTime, endDateTime, type, typeId, services, isDelivery, situationId, plate, client } = eventInfo.event._def.extendedProps;

  return(
    <SchedulingEvent
      title={(+typeId == 1) ? services[0]?.serviceName : title }
      quantity={quantity || services?.length}
      time={time == 'undefined - undefined' ? '-' : time}
      startDateTime={startDateTime}
      endDateTime={endDateTime}
      type={type}
      isDelivery={isDelivery}
      situationId={situationId}
      plate={plate}
      street={client?.street}
      name={client?.name}
      phone={client?.phones}
      email={client?.email}
    />
  );
}

export default function SchedulingOthersRegistration() {
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const history = useHistory();
  const isEdit = history.location.pathname == '/editar-agendamento';
  const id = history?.location?.state?.data?.id || 'new-event';
  const appointmentSelectionDate = history.location.state?.data?.appointmentSelectionDate;

  const didComponentMount = useDidMount();

  const calendarRef = useRef(null);
  const { unavailableEvents, handleDatesSet, dayCellClassNames, isDateUnavailable } = useSchedulingAvailability();

  const { token } = useSelector(state => state.login)
  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { selectedCalendarOption, calendarOptions, calendarList } = useSelector(state => state.scheduling);
  const { isLoading, submit } = useSelector(state => state.schedulingOthers);

  const [selectedCalendarEventId, setSelectedCalendarEventId] = useState('');
  const [selectedCalendarEventTypeId, setSelectedCalendarEventTypeId] = useState('');

  const isMonthView = selectedCalendarOption == 'dayGridMonth';
  const isWeekView = selectedCalendarOption == 'timeGridWeek';
  const isDayView = selectedCalendarOption == 'timeGridDay';

  const [dateCycle, setDateCycle] = useState(moment(moment.now()));

  const year = moment(dateCycle, 'YYYY-MM-DD').year();
  const month = moment().month(moment(dateCycle, 'YYYY-MM-DD').month()).format('MM');
  const maxDay = moment(dateCycle, 'YYYY-MM-DD').daysInMonth();

  const initialDate = `${year}-${month}-01`;

  const newEventSubmit = {
    dateStart: moment(moment.now()).format('YYYY-MM-DD'),
    dateEnd: moment(moment.now()).format('YYYY-MM-DD')
  }

  const [newEvents, setNewEvents] = useState([]);

  useEffect(() => {
    if(!token) {
      return history.push('/login');
    }

    if(token) {
      setToken(token);
    }
  }, [token]);

  useEffect(() => {
    if(isDayView) {
      setDateCycle(moment(calendarRef.current?.getApi()?.getDate()));
    }
  }, [isDayView]);

  useEffect(() => {
    const setDateTimeout = setTimeout(() => {
      if(!!appointmentSelectionDate) {
        formik.setFieldValue('dateStart', moment(appointmentSelectionDate, 'YYYY-MM-DD').format('YYYY-MM-DD'));
        formik.setFieldValue('dateEnd', moment(appointmentSelectionDate, 'YYYY-MM-DD').format('YYYY-MM-DD'));

        if(!isMonthView) {
          formik.setFieldValue('timeStart', moment(appointmentSelectionDate, 'YYYY-MM-DD HH:mm').format('HH:mm'));
          formik.setFieldValue('timeEnd', moment(appointmentSelectionDate, 'YYYY-MM-DD HH:mm').add(1, 'hour').format('HH:mm'));
        }

        setDateCycle(moment(appointmentSelectionDate, 'YYYY-MM-DD'));

        const calendarApi = calendarRef.current.getApi();
        calendarApi.gotoDate(moment(appointmentSelectionDate, 'YYYY-MM-DD HH:mm:ss').toDate());
      }
    }, 1000);

    return () => clearTimeout(setDateTimeout);
  }, [appointmentSelectionDate]);

  useEffect(() => {
    if(didComponentMount) {
      if(isDayView) {
        const start = moment(calendarRef.current?.getApi()?.getDate()).format('YYYY-MM-DD');
        const end = moment(calendarRef.current?.getApi()?.getDate()).format('YYYY-MM-DD');

        const extraProps = {
          initialDate: start,
          finalDate: end
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
      }

      if(isWeekView) {
        const { start, end } = getCalendarValidRange() || {};

        const extraProps = {
          initialDate: start,
          finalDate: end
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
      }

      if(isMonthView) {
        const { start, end } = getCalendarValidMonthRange();

        let extraProps = {
          initialDate: start,
          finalDate: isMonthView ? `${year}-${month}-${maxDay}` : end
        }

        if(!!appointmentSelectionDate) {
          const year = moment(appointmentSelectionDate, 'YYYY-MM-DD').year();
          const month = moment().month(moment(appointmentSelectionDate, 'YYYY-MM-DD').month()).format('MM');
          const maxDay = moment(appointmentSelectionDate, 'YYYY-MM-DD').daysInMonth();

          extraProps = {
            initialDate: `${year}-${month}-01`,
            finalDate: `${year}-${month}-${maxDay}`
          }
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
      }
    }
  }, []);

  useEffect(() => {
    if(!didComponentMount) {
      if(isDayView) {
        const start = moment(calendarRef.current?.getApi()?.getDate()).format('YYYY-MM-DD');
        const end = moment(calendarRef.current?.getApi()?.getDate()).format('YYYY-MM-DD');

        const extraProps = {
          initialDate: start,
          finalDate: end
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
      }

      if(isWeekView) {
        const { start, end } = getCalendarValidRange() || {};

        const extraProps = {
          initialDate: start,
          finalDate: end
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
      }

      if(isMonthView) {
        const { start, end } = getCalendarValidMonthRange() || {};

        const extraProps = {
          initialDate: start,
          finalDate: end
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, ''));
      }
    }
  }, [isDayView, isWeekView, isMonthView]);

  useEffect(() => {
    if(id && id != 'new-event') {
      dispatch(loadOthersScheduling(userId, establishmentId, id));
    }

    if(!id) {
      dispatch(clearSchedulingOthers());
    }
  }, [id]);

  useEffect(() => {
    if(calendarList?.length == 0) {
      setNewEvents([
        {
          id: 'new-event',
          title: !!formik.values?.title ? formik.values?.title : '',
          dateStart: moment(moment.now()).format('YYYY-MM-DD'),
          dateEnd: moment(moment.now()).format('YYYY-MM-DD'),
          timeStart: '',
          timeEnd: '',
          startDateTime: `${formik.values.dateStart} ${formik.values.timeStart}:00`,
          endDateTime: `${formik.values.dateEnd} ${formik.values.timeEnd}:00`,
          start: `${formik.values.dateStart} ${formik.values.timeStart}`,
          end: `${formik.values.dateEnd} ${formik.values.timeEnd}`,
          time: `${formik.values.timeStart} - ${formik.values.timeEnd}`
        }
      ]);
    }

    if(calendarList?.length > 0) {
      const formattedCalendarList = calendarList?.map(event => {
        return {
          ...event,
          title: event.title,
          start: moment(event.startDateTime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm:ss'),
          end: moment(event.endDateTime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm:ss'),
          time: `${moment(event.startDateTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')} - ${moment(event.endDateTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')}`
        }
      });

      let events = formattedCalendarList;

      if(id == 'new-event') {
        events = [
          ...events,
          {
            id: 'new-event',
            title: !!formik.values?.title ? formik.values?.title : '',
            dateStart: moment(moment.now()).format('YYYY-MM-DD'),
            dateEnd: moment(moment.now()).format('YYYY-MM-DD'),
            timeStart: '',
            timeEnd: '',
            startDateTime: `${formik.values.dateStart} ${formik.values.timeStart}:00`,
            endDateTime: `${formik.values.dateEnd} ${formik.values.timeEnd}:00`,
            start: `${formik.values.dateStart} ${formik.values.timeStart}`,
            end: `${formik.values.dateEnd} ${formik.values.timeEnd}`,
            time: `${formik.values.timeStart} - ${formik.values.timeEnd}`
          }
        ]
      }

      setNewEvents(events);
    }
  }, [calendarList]);

  const getCalendarValidRange = () => {
    if(isDayView) {
      return {
        start: dateCycle.format('YYYY-MM-DD'),
        end: dateCycle.format('YYYY-MM-DD')
      }
    }

    if(isWeekView) {
      return {
        start: dateCycle.startOf('week').format('YYYY-MM-DD'),
        end: dateCycle.clone().endOf('week').add(1, 'days').format('YYYY-MM-DD')
      }
    }
  }

  const getCalendarValidMonthRange = () => {
    const end = moment(dateCycle, 'YYYY-MM-DD').endOf('month').add(1, 'days').format('YYYY-MM-DD');

    return {
      start: initialDate,
      end
    }
  }

  const handleDateCycleChange = date => {
    if(isDayView) {
      if(date == 'prev') {
        const newDateCycle = moment(dateCycle.subtract(1, 'days'));
        setDateCycle(newDateCycle);

        const extraProps = {
          initialDate: newDateCycle.format('YYYY-MM-DD'),
          finalDate: newDateCycle.format('YYYY-MM-DD')
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, '')).then(() => {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.prev();
        });
      }

      if(date == 'next') {
        const newDateCycle = moment(dateCycle.add(1, 'days'));
        setDateCycle(newDateCycle);

        const extraProps = {
          initialDate: newDateCycle.format('YYYY-MM-DD'),
          finalDate: newDateCycle.format('YYYY-MM-DD')
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, '')).then(() => {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.next();
        });
      }
    }

    if(isWeekView) {
      if(date == 'prev') {
        return setDateCycle(moment(dateCycle.subtract(1, 'week')));
      }
  
      if(date == 'next') {
        return setDateCycle(moment(dateCycle.add(1, 'week')));
      }
    }

    if(isMonthView) {
      if(date == 'prev') {
        const newDateCycle = moment(dateCycle.subtract(1, 'month'));
        setDateCycle(newDateCycle);

        const extraProps = {
          initialDate: newDateCycle.startOf('month').format('YYYY-MM-DD'),
          finalDate: newDateCycle.endOf('month').format('YYYY-MM-DD')
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, '')).then(() => {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.prev();
        });
      }
  
      if(date == 'next') {
        const newDateCycle = moment(dateCycle.add(1, 'month'));
        setDateCycle(newDateCycle);

        const extraProps = {
          initialDate: newDateCycle.startOf('month').format('YYYY-MM-DD'),
          finalDate: newDateCycle.endOf('month').format('YYYY-MM-DD')
        }

        dispatch(loadCalendarServices(userId, establishmentId, extraProps, '')).then(() => {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.next();
        });
      }
    }
  }

  const handleSchedulingSubmit = values => {
    const params = {
      title: values?.title,
      startDateTime: `${values?.dateStart} ${values?.timeStart}:00`,
      endDateTime: `${values?.dateEnd} ${values?.timeEnd}:00`,
      situationId: 1,
      typeId: 2,
      observation: values?.observation
    }

    dispatch(upsertOthersScheduling(userId, establishmentId, values?.id, params)).then(() => {
      formik.resetForm();

      if(isSm) {
        return history.push('/agendamento');
      }
    });
  }

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: validations,
    initialValues: (id == 'new-event') ? newEventSubmit : submit,
    onSubmit: values => handleSchedulingSubmit(values)
  });

  useEffect(() => {
    if(formik.values.dateStart && formik.values.dateEnd && formik.values.timeStart && formik.values.timeEnd) {
      const updatedEvents = newEvents.map(event => {
        if(event?.id == id) {
          return {
            ...event,
            title: formik.values.title || '',
            startDateTime: `${formik.values.dateStart} ${formik.values.timeStart}:00`,
            endDateTime: `${formik.values.dateEnd} ${formik.values.timeEnd}:00`,
            start: `${formik.values.dateStart} ${formik.values.timeStart}`,
            end: `${formik.values.dateEnd} ${formik.values.timeEnd}`,
            time: `${formik.values.timeStart} - ${formik.values.timeEnd}`
          }
        }

        return event;
      });

      setNewEvents(updatedEvents);
    }
  }, [formik.values, dateCycle]);

  return(
    <Paper style={{ height: '100%' }}>
      {!!selectedCalendarEventId && (
        <Modal
          id='scheduling-calendar-event-edit'
          title='Edição de evento'
          maxWidth='sm'
          open={!!selectedCalendarEventId}
          onClose={() => {
            setSelectedCalendarEventId('');
            setSelectedCalendarEventTypeId('');
          }}
        >
          <Typography color={theme.palette.primary.main}>
            Deseja editar este evento?
          </Typography>
          <Grid container xs={12} justifyContent='flex-end' alignItems='center' gap='10px'>
            <Button
              type='button'
              color={theme.palette.success.main}
              onClick={() => {
                dispatch(setUserId(userId));
                dispatch(setEstablishmentId({ establishmentId }));

                if(selectedCalendarEventTypeId == 1) {
                  history.push({
                    pathname: '/editar-agendamento-servico',
                    state: {
                      data: {
                        id: selectedCalendarEventId
                      }
                    }
                  });

                  setSelectedCalendarEventId('');
                  setSelectedCalendarEventTypeId('');
                }

                if(selectedCalendarEventTypeId == 2) {
                  history.push({
                    pathname: '/editar-agendamento',
                    state: {
                      data: {
                        id: selectedCalendarEventId
                      }
                    }
                  });

                  setSelectedCalendarEventId('');
                  setSelectedCalendarEventTypeId('');
                }
              }}
            >
              Sim
            </Button>
            <Button
              type='button'
              color={theme.palette.error.main}
              onClick={() => {
                setSelectedCalendarEventId('');
                setSelectedCalendarEventTypeId('');
              }}
            >
              Não
            </Button>
          </Grid>
        </Modal>
      )}
      <Grid container style={{ height: '100%' }}>
        <Grid item xs={isSm ? 12 : 5} md={isSm ? 12 : 3} style={{ backgroundColor: '#FCFCFC', height: '100vh', padding: 10 }}>
          <form
            onSubmit={formik.handleSubmit}
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
          >
            <div style={{ height: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img src={DrawerIcon} />
                  <Typography color='textSecondary' style={{ marginLeft: 5 }}>
                    {isEdit ? 'Editar agendamento' : 'Novo agendamento'}
                  </Typography>
                </div>
                <div>
                  <IconButton onClick={() => history.push('/agendamento')}>
                    <CloseIcon />
                  </IconButton>
                </div>
              </div>
              <Loader isLoading={isLoading} style={{ height: '100%' }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Input
                      type='text'
                      name='title'
                      label='Nome do agendamento'
                      value={formik.values.title}
                      error={formik.errors.title}
                      helperText={formik.errors.title}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        inputFormat='DD/MM/YYYY'
                        value={formik.values.dateStart}
                        onChange={date => {
                          formik.setFieldValue('dateStart', moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                          formik.setFieldValue('dateEnd', moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                        }}
                        components={{
                          OpenPickerIcon: () => (
                            <EventNote style={{ color: '#1E5168' }} />
                          )
                        }}
                        InputProps={{
                          endAdornment: isSm && (
                            <EventNote style={{ color: '#1E5168' }} />
                          )
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            name='dateStart'
                            variant='outlined'
                            error={formik.errors.dateStart}
                            helperText={formik.errors.dateStart}
                            inputProps={{
                              ...params.inputProps,
                              style: {
                                height: 20,
                                color: theme.palette.primary.main
                              }
                            }}
                            SelectProps={{
                              MenuProps: {
                                disableEnforceFocus: true
                              }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main
                                }
                              }
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={6}>
                    <Input
                      type='timeRangePadStart'
                      name='timeStart'
                      label='Hora início'
                      value={formik.values.timeStart || ''}
                      error={formik.errors.timeStart}
                      helperText={formik.errors.timeStart}
                      onChange={event => {
                        if(event.target.value?.length > 5) {
                          return;
                        }

                        formik.setFieldValue('timeStart', event.target.value);
                      }}
                      endAdornment={
                        <InputAdornment position='end'>
                          <FontAwesomeIcon
                            color='#022A5C'
                            size='sx'
                            icon={faClock}
                          />
                        </InputAdornment>
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        inputFormat='DD/MM/YYYY'
                        value={formik.values.dateEnd}
                        onChange={date => {
                          if(moment(date, 'DD/MM/YYYY').isBefore(moment(formik.values.dateStart, 'YYYY-MM-DD'))) {
                            return toast.error('A data final precisa ser maior que a data inicial.');
                          }

                          formik.setFieldValue('dateEnd', moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD'));
                        }}
                        components={{
                          OpenPickerIcon: () => (
                            <EventNote style={{ color: '#1E5168' }} />
                          )
                        }}
                        InputProps={{
                          endAdornment: isSm && (
                            <EventNote style={{ color: '#1E5168' }} />
                          )
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            name='dateEnd'
                            variant='outlined'
                            error={formik.errors.dateEnd}
                            helperText={formik.errors.dateEnd}
                            inputProps={{
                              ...params.inputProps,
                              style: {
                                height: 20,
                                color: theme.palette.primary.main
                              }
                            }}
                            SelectProps={{
                              MenuProps: {
                                disableEnforceFocus: true
                              }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main
                                }
                              }
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={6}>
                    <Input
                      type='timeRangePadStart'
                      name='timeEnd'
                      label='Hora fim'
                      value={formik.values.timeEnd || ''}
                      error={formik.errors.timeEnd}
                      helperText={formik.errors.timeEnd}
                      onChange={event => {
                        if(event.target.value?.length > 5) {
                          return;
                        }

                        formik.setFieldValue('timeEnd', event.target.value);
                      }}
                      endAdornment={
                        <InputAdornment position='end'>
                          <FontAwesomeIcon
                            color='#022A5C'
                            size='sx'
                            icon={faClock}
                          />
                        </InputAdornment>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Input
                      multiline
                      type='text'
                      name='observation'
                      label='Observação'
                      value={formik.values.observation}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                </Grid>
              </Loader>
            </div>
            {!isLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <div style={{ width: isSm ? '50%' : 'fit-content' }}>
                  <Button
                    fullWidth={isSm}
                    type='button'
                    color='tertiary'
                    variant='outlined'
                    onClick={() => history.push('/agendamento')}
                  >
                    Cancelar
                  </Button>
                </div>
                <div style={{ width: isSm ? '50%' : 'fit-content' }}>
                  <Button
                    fullWidth={isSm}
                    type='submit'
                    color='primary'
                  >
                    {isEdit ? 'Editar' : 'Salvar'}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </Grid>
        {!isSm && (
          <Grid item xs={7} md={9} style={{ height: '100%' }}>
            <Grid container>
              <Grid item xs={12} style={{ height: '5vh' }}>
                <Grid container>
                  <Grid item xs={10} style={{ display: 'flex', alignItems: 'center', paddingLeft: 10 }}>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <IconButton
                        style={{ backgroundColor: 'rgba(21, 56, 72, 0.2)', color: theme.palette.primary.main }}
                        onClick={() => handleDateCycleChange('prev')}
                      >
                        <KeyboardArrowLeft />
                      </IconButton>
                      <IconButton
                        style={{ backgroundColor: 'rgba(21, 56, 72, 0.2)', color: theme.palette.primary.main }}
                        onClick={() => handleDateCycleChange('next')}
                      >
                        <KeyboardArrowRight />
                      </IconButton>
                    </div>
                    <Typography
                      style={{ marginLeft: 10 }}
                      color={theme.palette.primary.main}
                      variant='h6'
                    >
                      {isDayView && (
                        <>
                          {dateCycle.format('DD/MMM')}
                        </>
                      )}
                      {isWeekView && (
                        <>
                          {dateCycle.startOf('week').format('DD/MMM')} - {dateCycle.endOf('week').format('DD/MMM')}
                        </>
                      )}
                      {isMonthView && (
                        <>
                          {capitalizeString(dateCycle.format('MMMM'))} {dateCycle.format('YYYY')}
                        </>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} style={{ paddingRight: 5 }}>
                    <Select
                      options={calendarOptions}
                      value={selectedCalendarOption}
                      onChange={event => dispatch(setCalendarOption(event.target.value))}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ height: '95vh', padding: '0 5px 5px 5px' }}>
                {(selectedCalendarOption == 'dayGridMonth') && (
                  <FullCalendar
                    ref={calendarRef}
                    locale='pt-br'
                    plugins={[ interactionPlugin, dayGridPlugin, timeGridPlugin ]}
                    headerToolbar={{
                      start: '',
                      center: '',
                      end: ''
                    }}
                    dayHeaderContent={option => {
                      if(isMonthView) {
                        return(
                          <div>
                            <Typography color='textSecondary' variant='body2'>
                              {capitalizeString(moment(option.date).add(1, 'day').format('ddd'))}
                            </Typography>
                          </div>
                        );
                      }

                      if(!isMonthView) {
                        return(
                          <div>
                            {option.isToday ? (
                              <Chip
                                style={{ height: 20, backgroundColor: '#13BC4B' }}
                                label={
                                  <Typography style={{ color: '#FFFFFF' }}>
                                    {moment(option.date).format('DD/MMM')}
                                  </Typography>
                                }
                              />
                            ) : (
                              <Typography color={theme.palette.primary.main}>
                                {moment(option.date).format('DD/MMM')}
                              </Typography>
                            )}
                            <Typography color='textSecondary' variant='body2'>
                              {moment(option.date).format('ddd').toUpperCase()}
                            </Typography>
                          </div>
                        );
                      }
                    }}
                    dayCellContent={option => {
                      if(isMonthView) {
                        return(
                          <div>
                            {option.isToday ? (
                              <div style={{ textAlign: 'end' }}>
                                <Chip
                                  style={{ height: 20, backgroundColor: '#13BC4B' }}
                                  label={
                                    <Typography style={{ color: '#FFFFFF' }}>
                                      {moment(option.date).format('DD')}
                                    </Typography>
                                  }
                                />
                              </div>
                            ) : (
                              <div style={{ textAlign: 'end' }}>
                                <Typography color={theme.palette.primary.main}>
                                  {moment(option.date).format('DD')}
                                </Typography>
                              </div>
                            )}
                          </div>
                        );
                      }
                    }}
                    datesSet={handleDatesSet}
                    dayCellClassNames={dayCellClassNames}
                    events={newEvents?.map(list => {
                      return {
                        ...list,
                        start: moment(list?.startDateTime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD'),
                        end: moment(list?.endDateTime, 'YYYY-MM-DD HH:mm:ss').add(1, 'days').format('YYYY-MM-DD')
                      }
                    })}
                    eventContent={renderEventContent}
                    eventOrder={function (a, b) {
                      return moment(a.startDateTime, 'YYYY-MM-DD HH:mm:ss') - moment(b.startDateTime, 'YYYY-MM-DD HH:mm:ss')
                    }}
                    eventClick={({ event }) => {
                      if(event.extendedProps?.isUnavailableSlot) {
                        return;
                      }

                      const { type } = event._def.extendedProps;
                      const { publicId } = event._def;

                      setSelectedCalendarEventTypeId(type);
                      setSelectedCalendarEventId(publicId);
                    }}
                    dateClick={({ date, allDay }) => {
                      if(isDateUnavailable(date, allDay)) {
                        return;
                      }

                      const calendarApi = calendarRef.current.getApi();
                      calendarApi.gotoDate(date);

                      setDateCycle(moment(date));

                      formik.setFieldValue('dateStart', moment(date).format('YYYY-MM-DD'));
                      formik.setFieldValue('dateEnd', moment(date).format('YYYY-MM-DD'));
                    }}
                    dayMaxEvents={6}
                    moreLinkContent='Mais eventos'
                    initialView='dayGridMonth'
                    validRange={{}}
                    firstDay={1}
                    allDaySlot={false}
                    expandRows={true}
                    fixedWeekCount={false}
                    height='100%'
                  />
                )}
                {(selectedCalendarOption == 'timeGridWeek') && (
                  <FullCalendar
                    ref={calendarRef}
                    locale='pt-br'
                    plugins={[ interactionPlugin, dayGridPlugin, timeGridPlugin ]}
                    headerToolbar={{
                      start: '',
                      center: '',
                      end: ''
                    }}
                    dayHeaderContent={option => {
                      if(isMonthView) {
                        return(
                          <div>
                            <Typography color='textSecondary' variant='body2'>
                              {capitalizeString(moment(option.date).add(1, 'day').format('ddd'))}
                            </Typography>
                          </div>
                        );
                      }

                      if(!isMonthView) {
                        return(
                          <div>
                            {option.isToday ? (
                              <Chip
                                style={{ height: 20, backgroundColor: '#13BC4B' }}
                                label={
                                  <Typography style={{ color: '#FFFFFF' }}>
                                    {moment(option.date).format('DD/MMM')}
                                  </Typography>
                                }
                              />
                            ) : (
                              <Typography color={theme.palette.primary.main}>
                                {moment(option.date).format('DD/MMM')}
                              </Typography>
                            )}
                            <Typography color='textSecondary' variant='body2'>
                              {moment(option.date).format('ddd').toUpperCase()}
                            </Typography>
                          </div>
                        );
                      }
                    }}
                    dayCellContent={option => {
                      if(isMonthView) {
                        return(
                          <div>
                            {option.isToday ? (
                              <div style={{ textAlign: 'end' }}>
                                <Chip
                                  style={{ height: 20, backgroundColor: '#13BC4B' }}
                                  label={
                                    <Typography style={{ color: '#FFFFFF' }}>
                                      {moment(option.date).format('DD')}
                                    </Typography>
                                  }
                                />
                              </div>
                            ) : (
                              <div style={{ textAlign: 'end' }}>
                                <Typography color={theme.palette.primary.main}>
                                  {moment(option.date).format('DD')}
                                </Typography>
                              </div>
                            )}
                          </div>
                        );
                      }
                    }}
                    datesSet={handleDatesSet}
                    dayCellClassNames={dayCellClassNames}
                    events={[
                      ...unavailableEvents,
                      ...(newEvents?.map(list => {
                        delete list['allDay'];
                        return list;
                      }) || [])
                    ]}
                    eventContent={renderEventContent}
                    eventClick={({ event }) => {
                      if(event.extendedProps?.isUnavailableSlot) {
                        return;
                      }

                      const { type } = event._def.extendedProps;
                      const { publicId } = event._def;

                      setSelectedCalendarEventTypeId(type);
                      setSelectedCalendarEventId(publicId);
                    }}
                    dateClick={({ date, allDay }) => {
                      if(isDateUnavailable(date, allDay)) {
                        return;
                      }

                      const calendarApi = calendarRef.current.getApi();
                      calendarApi.gotoDate(date);

                      setDateCycle(moment(date));

                      formik.setFieldValue('dateStart', moment(date).format('YYYY-MM-DD'));
                      formik.setFieldValue('dateEnd', moment(date).format('YYYY-MM-DD'));
                      formik.setFieldValue('timeStart', moment(date).format('HH:mm'));
                      formik.setFieldValue('timeEnd', moment(date).add(1, 'hour').format('HH:mm'));
                    }}
                    initialView='timeGridWeek'
                    validRange={getCalendarValidRange}
                    firstDay={null}
                    allDaySlot={false}
                    expandRows={true}
                    fixedWeekCount={false}
                    height='100%'
                  />
                )}
                {(selectedCalendarOption == 'timeGridDay') && (
                  <FullCalendar
                    ref={calendarRef}
                    locale='pt-br'
                    plugins={[ interactionPlugin, dayGridPlugin, timeGridPlugin ]}
                    headerToolbar={{
                      start: '',
                      center: '',
                      end: ''
                    }}
                    dayHeaderContent={option => {
                      if(isMonthView) {
                        return(
                          <div>
                            <Typography color='textSecondary' variant='body2'>
                              {capitalizeString(moment(option.date).add(1, 'day').format('ddd'))}
                            </Typography>
                          </div>
                        );
                      }

                      if(!isMonthView) {
                        return(
                          <div>
                            {option.isToday ? (
                              <Chip
                                style={{ height: 20, backgroundColor: '#13BC4B' }}
                                label={
                                  <Typography style={{ color: '#FFFFFF' }}>
                                    {moment(option.date).format('DD/MMM')}
                                  </Typography>
                                }
                              />
                            ) : (
                              <Typography color={theme.palette.primary.main}>
                                {moment(option.date).format('DD/MMM')}
                              </Typography>
                            )}
                            <Typography color='textSecondary' variant='body2'>
                              {moment(option.date).format('ddd').toUpperCase()}
                            </Typography>
                          </div>
                        );
                      }
                    }}
                    dayCellContent={option => {
                      if(isMonthView) {
                        return(
                          <div>
                            {option.isToday ? (
                              <div style={{ textAlign: 'end' }}>
                                <Chip
                                  style={{ height: 20, backgroundColor: '#13BC4B' }}
                                  label={
                                    <Typography style={{ color: '#FFFFFF' }}>
                                      {moment(option.date).format('DD')}
                                    </Typography>
                                  }
                                />
                              </div>
                            ) : (
                              <div style={{ textAlign: 'end' }}>
                                <Typography color={theme.palette.primary.main}>
                                  {moment(option.date).format('DD')}
                                </Typography>
                              </div>
                            )}
                          </div>
                        );
                      }
                    }}
                    datesSet={handleDatesSet}
                    dayCellClassNames={dayCellClassNames}
                    events={[
                      ...unavailableEvents,
                      ...(newEvents?.map(list => {
                        delete list['allDay'];
                        return list;
                      }) || [])
                    ]}
                    eventContent={renderEventContent}
                    eventClick={({ event }) => {
                      if(event.extendedProps?.isUnavailableSlot) {
                        return;
                      }

                      const { type } = event._def.extendedProps;
                      const { publicId } = event._def;

                      setSelectedCalendarEventTypeId(type);
                      setSelectedCalendarEventId(publicId);
                    }}
                    dateClick={({ date, allDay }) => {
                      if(isDateUnavailable(date, allDay)) {
                        return;
                      }

                      const calendarApi = calendarRef.current.getApi();
                      calendarApi.gotoDate(date);

                      setDateCycle(moment(date));

                      formik.setFieldValue('dateStart', moment(date).format('YYYY-MM-DD'));
                      formik.setFieldValue('dateEnd', moment(date).format('YYYY-MM-DD'));
                      formik.setFieldValue('timeStart', moment(date).format('HH:mm'));
                      formik.setFieldValue('timeEnd', moment(date).add(1, 'hour').format('HH:mm'));
                    }}
                    initialView='timeGridDay'
                    validRange={{}}
                    firstDay={null}
                    allDaySlot={false}
                    expandRows={true}
                    fixedWeekCount={false}
                    height='100%'
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}